import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
const inputCls = "w-full bg-slate-950/60 border border-slate-800 rounded px-2 py-1 font-mono text-slate-100 text-xs";
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const invoiceStateClass = (s) => {
  if (s === "paid") return "text-emerald-400";
  if (s === "open") return "text-amber-300";
  return "text-slate-400";
};
const lastMonth = () => { const d = new Date(); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() - 1); return d.toISOString().slice(0, 7); };
const fail = (fallback) => (e) => toast.error(e?.response?.data?.detail || fallback);

function BillingSettingsForm({ t }) {
  const [form, setForm] = useState({ billing_email: t.billing_email || "", auto_invoice: !!t.auto_invoice, invoice_days_until_due: t.invoice_days_until_due || 30 });
  const save = async () => { try { await api.put(`/platform/partners/${t.id}/billing`, { ...form, invoice_days_until_due: Number(form.invoice_days_until_due) || 30 }); toast.success("Billing settings saved"); } catch (e) { fail("Failed")(e); } };
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
      <label><span className="text-slate-400">Billing email</span><input value={form.billing_email} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} className={inputCls} data-testid="billing-email-input" /></label>
      <label><span className="text-slate-400">Days until due</span><input value={form.invoice_days_until_due} onChange={(e) => setForm({ ...form, invoice_days_until_due: e.target.value })} className={inputCls} /></label>
      <label className="flex items-center gap-2 pb-1"><input type="checkbox" checked={form.auto_invoice} onChange={(e) => setForm({ ...form, auto_invoice: e.target.checked })} data-testid="auto-invoice-toggle" /> Auto-invoice on the 1st of each month</label>
      <button onClick={save} className="bg-slate-800 hover:bg-slate-700 text-slate-100 rounded px-3 py-1.5" data-testid="save-billing-button">Save billing</button>
    </div>
  );
}

function InvoiceList({ invoices }) {
  return (
    <table className="sc-table w-full mt-3"><thead><tr><th>Month</th><th>Invoice</th><th>Total</th><th>Status</th><th>Created</th><th></th></tr></thead>
      <tbody>{invoices.map((i) => <tr key={i.month} data-testid={`invoice-row-${i.month}`}><td className="font-mono">{i.month}</td><td className="font-mono">{i.number || i.stripe_invoice_id || "—"}</td><td className="font-mono">{money(i.total)}</td><td><span className={invoiceStateClass(i.state)}>{i.state}</span></td><td className="text-slate-400">{i.created_at?.slice(0, 10)} {i.created_by?.startsWith("cron") ? "· auto" : ""}</td><td>{i.hosted_invoice_url && <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-amber-400">Open</a>}</td></tr>)}
        {!invoices.length && <tr><td colSpan={6} className="text-slate-500">No invoices yet.</td></tr>}</tbody></table>
  );
}

export function BillingPanel({ t }) {
  const [invoices, setInvoices] = useState([]);
  const [month, setMonth] = useState(lastMonth);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/platform/partners/${t.id}/invoices`).then((r) => setInvoices(r.data)), [t.id]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/platform/partners/${t.id}/invoice`, { month }); if (data.skipped) toast.info(data.reason); else toast.success(data.duplicate ? "Invoice already exists for that month" : `Invoice ${data.number} sent — ${money(data.amount_due)}`); load(); }
    catch (e) { fail("Invoice failed")(e); } finally { setBusy(false); }
  };
  return (
    <div className="border border-slate-800 rounded p-3 bg-slate-950/40" data-testid="partner-billing-panel">
      <div className="label-cap mb-2">Partner invoicing (Stripe)</div>
      <BillingSettingsForm t={t} />
      <div className="flex items-center gap-3 mt-3">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={inputCls + " w-40"} data-testid="invoice-month-input" />
        <button onClick={create} disabled={busy || !t.billing_email} className="bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-slate-950 font-semibold rounded px-3 py-1.5" data-testid="create-invoice-button">{busy ? "Creating…" : "Create & send Stripe invoice for month"}</button>
      </div>
      <InvoiceList invoices={invoices} />
    </div>
  );
}
